/**
 * @module components/Accordion
 * @description Flexible accordion/collapsible component system with nested support.
 * Supports both checkbox (multiple open) and radio (single open) modes with state persistence
 * across navigation via pageStateStack integration.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { usePageState } from '@/providers/SavedPageState';

const RadioGroupNameContext = React.createContext();
const AccordionContext = React.createContext();

/**
 * @function useAccordionContext
 * @description React Hook to access accordion context for nested accordions.
 * Provides `isClosed` state to manage keyboard navigation and nested behavior.
 * @returns {Object} AccordionContext with isClosed property
 */
export const useAccordionContext = () => useContext(AccordionContext);

/**
 * If the props.label is not unique, pass a props.id instead
 */

/**
 * @typedef AccordionProps
 * @prop {string} [id] HTML `id` attribute for inner <input> (optional)
 * @prop {string} [className] CSS class name (optional)
 * @prop {string} label text label for input field (if not unique, also pass `id`), will be used as `id` if not specified
 * @prop {string} labelClass CSS class to use for <label>
 * @prop {boolean} [startOpen] start with accordion open or not (optional)
 * @prop {ReactNode} children
 */

/**
 * @component Accordion
 * @type {React.FC<AccordionProps>}
 * @param {AccordionProps} props
 * @summary React Component for accordion input, used as tabs, may be nested, highly versatile. Must be wrapped in <AccordionGroup>. Is a checkbox or radio <input> that will trigger an associated accordion tab.
 * @returns {ReactNode}
 */
const Accordion = (props) => {
  const parentAccordion = useContext(AccordionContext);
  const { groupName } = useContext(RadioGroupNameContext) || {};
  const [hasMounted, setHasMounted] = useState(false);
  const content = useRef({});
  const [maxHeight, setMaxHeight] = useState(0);
  const [isOpen, _setIsOpen] = useState(props.startOpen);
  const [pageStateOnPageLoad, updatePageState] = usePageState();

  const hiddenCheckbox = useRef();

  /**
   * set initial page state, which accordions are opened or not
   */
  useEffect(() => {
    Object.entries(pageStateOnPageLoad).forEach(([key, val]) => {
      if ((props.id === key || props.label === key) && val) {
        _setIsOpen(val);
        hiddenCheckbox.current.checked = val;
      }
    });
  }, [pageStateOnPageLoad, props.id, props.label]);

  /**
   * echo `open` status to page + component state
   */
  const setIsOpen = useCallback(
    (_isOpen) => {
      updatePageState(props.id || props.label, _isOpen);
      _setIsOpen(_isOpen);
    },
    [updatePageState, props.id, props.label],
  );

  const hasRetried = useRef(false);

  /**
   * measure page height to set max height of accordion tab content
   */
  const measureHeight = useCallback(() => {
    const thisNode = content?.current;
    if (thisNode) {
      setMaxHeight(
        [
          thisNode,
          ...thisNode.querySelectorAll('.accordion-tab-content'),
        ].reduce((total, node) => (total += node.scrollHeight), 0),
      );
    } else if (!hasRetried.current) {
      // trigger another measure
      hasRetried.current = true;
      setTimeout(() => setHasMounted('retry'), 240);
    }
  }, []);

  const debounceTimeout = useRef();
  const debouncedMeasure = useCallback(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(measureHeight, 240);
  }, [measureHeight]);

  useEffect(() => {
    if (!hasMounted) setHasMounted(true);
    debouncedMeasure();
    window.addEventListener('resize', debouncedMeasure);
    return () => {
      window.removeEventListener('resize', debouncedMeasure);
    };
  }, [hasMounted, debouncedMeasure]);

  const handleKeyPress = useCallback(
    (e) => {
      if ([' ', 'Enter'].includes(e.key)) {
        e.preventDefault();
        setIsOpen((wasOpen) => {
          hiddenCheckbox.current.checked = !wasOpen;
          return !wasOpen;
        });
      }
    },
    [setIsOpen],
  );

  const contextValue = useMemo(
    () => ({
      isClosed: !isOpen,
    }),
    [isOpen],
  );

  return (
    <div className={props.className}>
      <input
        type={groupName ? 'radio' : 'checkbox'}
        className="accordion-input"
        ref={hiddenCheckbox}
        tabIndex={-1}
        id={props.id || props.label}
        name={groupName}
        onChange={({ target }) => setIsOpen(target.checked)}
        defaultChecked={props.startOpen}
      />

      <label
        tabIndex={parentAccordion?.isClosed ? -1 : 0}
        onKeyPress={handleKeyPress}
        htmlFor={props.id || props.label}
        className={cx('accordion-tab-label semi akz-e py025', props.labelClass)}
      >
        {props.label}
      </label>

      <div
        className="accordion-tab-content"
        style={{ maxHeight: isOpen ? maxHeight : 0 }}
        ref={content}
      >
        <AccordionContext.Provider value={contextValue}>
          <RadioGroupNameContext.Provider value={null}>
            {props.children}
          </RadioGroupNameContext.Provider>
        </AccordionContext.Provider>
      </div>
    </div>
  );
};

/**
 * @typedef AccordionGroupProps
 * @prop {string} groupName name for accordion group
 * @prop {ReactNode} children
 * @returns {React.Provider<AccordionGroupProps>}
 */

/**
 * @component AccordionGroup
 * @type {React.FC<AccordionGroupProps>}
 * @summary React Component for Accordion Group Context Provider (wraps <Accordion>). If `groupName` is specified, the child accordions will be radio buttons (i.e., only one can be opened at a time).
 * @param {AccordionGroupProps} props
 * @returns {React.Provider<AccordionGroupProps>} React Context Provider with `groupName` as value + children
 */
export const AccordionGroup = ({ groupName, children }) => (
  <RadioGroupNameContext.Provider value={{ groupName }}>
    {children}
  </RadioGroupNameContext.Provider>
);

export default Accordion;
